import MarkdownRenderer from "@/components/ai-chatbox/markdown-renderer";
import { modalStore } from "@/lib/stores/modals";
import { useStore } from "@tanstack/react-store";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "~/components/ui/dialog";

export default function Announcement() {
	const { ppModalOpen } = useStore(modalStore);

	return (
		<div>
			<Dialog
				open={ppModalOpen}
				onOpenChange={(open) => modalStore.setPPModalOpen(open)}
			>
				<DialogContent className="md:!w-[850px] md:!max-w-[850px] md:max-h-[658px]">
					<DialogHeader>
						<DialogTitle>Privacy Policy and Terms & Conditions</DialogTitle>
					</DialogHeader>

					<div className="text-t1 mt-4 pr-6 wrap-anywhere overflow-auto max-h-[500px]">
						<MarkdownRenderer>
							{`
# Privacy Policy

**Effective Date: 7/7/2025**

Welcome to PeachLust. We are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our AI-generated content services.

By using our Site and Services, you agree to the collection and use of information in accordance with this Privacy Policy.

---
## Information We Collect
We collect the following types of personal and usage data:

**a. Information You Provide to Us Directly**
- Email address
- Name or nickname
- Payment information (if applicable; processed via third-party payment providers)

**b. Automatically Collected Information**
- IP address
- Unique device identifier
- Device type, such as your phone, computer, or tablet
- Browser type and version
- Operating system
- Date/time of access
- Usage data (e.g. input queries, session duration)

**c. Cookies and Tracking Technologies**
 We use cookies and similar tracking technologies to enhance your experience, analyze usage, and deliver personalized content. You can manage your cookie preferences through your browser settings.

---
## How We Use Your Information
We use the collected data to:
- Provide and maintain our Services
- Improve and personalize your experience
- Respond to inquiries and support requests
- Detect, prevent, and address technical issues or abuse
- Send you service-related emails (only with your consent, where required)

---
## Legal Basis for Processing (EU Users)
If you are located in the European Economic Area (EEA), our legal bases for processing your data include:
- Your consent (e.g. marketing communications, cookies)
- Fulfillment of a contract (e.g. providing AI services)
- Compliance with legal obligations
- Legitimate interests (e.g. to improve our services)

---
## Sharing Your Information
We do **not** sell your personal data.
We may share data with:
- **Service providers** (e.g. cloud storage, payment processors)
- **Legal authorities**, if required by law or to protect our rights
- **Analytics providers**, such as Google Analytics

All third parties are required to handle your data securely and in accordance with this Privacy Policy.

---
## Data Retention
We retain your information only for as long as necessary to fulfill the purposes described in this policy or as required by law. You may request deletion of your data at any time.

---
## Your Rights
Depending on your location, you may have the following rights:
### Under GDPR (EU/EEA Users):
- Access to your data
- Correction of inaccurate data
- Deletion of your data
- Restriction or objection to processing
- Data portability
- Withdrawal of consent
### Under CCPA (California Residents):
- Right to know what personal data is collected
- Right to request deletion
- Right to opt out of data sales (we do not sell your data)
- Right to non-discrimination for exercising rights
To exercise any of these rights, please contact us at: **contact@peachlust.com**

---
### User Generated Content (UGC) Policy
Our platform allows users to create, submit, and share content, including but not limited to comments, text inputs, game interactions, character settings, and any other materials uploaded by users (collectively referred to as “User Generated Content” or “UGC”). Please read and understand the following terms before submitting any UGC:

**1. No Prior Review or Moderation：**
We **do not pre-screen, monitor, or moderate** user-generated content. The platform is designed to support open and creative expression, and all UGC is submitted and published by users at their own discretion. Any UGC displayed or made available on the platform is the sole responsibility of the user who created it and **does not reflect the views or opinions of the platform**. We **do not endorse, verify, or guarantee** the legality, accuracy, or appropriateness of any user content.

**2. User Responsibility for Content Legality and Compliance**
By submitting UGC, **you agree that you are solely responsible** for ensuring that your content complies with all applicable laws, regulations, and third-party rights. You **must not submit content** that:
- Infringes any intellectual property rights, privacy rights, publicity rights, or other legal rights of any third party;
- Violates any local, national, or international laws or regulations;
- Contains illegal, obscene, pornographic, hateful, harassing, violent, or deceptive material;
- Contains viruses, malware, or harmful code that could compromise platform integrity.

You acknowledge and agree that **you will bear full legal and financial responsibility** for any consequences arising from your submitted content, and that the platform **shall not be held liable** for any claims or disputes related to your UGC.

**3. Public Nature of UGC**
Unless explicitly stated otherwise, any UGC you submit may be made publicly accessible on the platform and may be viewed, shared, or commented on by other users. Please **do not submit sensitive personal or confidential information** that you do not wish to be made public.

We are not responsible for the actions of other users with respect to the content you choose to share, nor can we guarantee that your privacy will be respected by third parties who access or redistribute your UGC.

**4. License Grant**
By submitting UGC to the platform, you grant us a **worldwide, non-exclusive, royalty-free, transferable, sublicensable license** to use, reproduce, distribute, display, perform, publish, translate, adapt, and create derivative works from your content for the purposes of operating, promoting, and improving the platform and related services.

We will not misrepresent authorship of your content, but you acknowledge that we are not required to credit or attribute you in every instance of use.

**5. Reporting and Removal**
While we do not actively monitor all user submissions, we reserve the right—but not the obligation—to remove or restrict any content that we, in our sole discretion, believe may violate this policy or applicable law.

To request removal of content or report UGC that you believe violates this policy, please contact us at contact@peachlust.com.

---
## Children's Privacy
Our services are not intended for individuals under the age of 18. We do not knowingly collect data from children. If we learn that we have collected such data, we will delete it promptly.

---
## Data Security
We implement industry-standard technical and organizational measures to protect your data. However, no method of transmission or storage is 100% secure.

---
## International Transfers
Your information may be transferred to—and maintained on—servers located outside of your country. We ensure that such transfers comply with applicable data protection laws.

---
## Changes to This Privacy Policy
We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated “Effective Date.” If significant changes are made, we will notify you by email or other appropriate means.IF YOU DO NOT ACCEPT AND AGREE TO THIS PRIVACY POLICY, INCOULDING ANY CHANGES, THEN YOU MUST NOT ACCESS OR USE THE SERVICES.

---


## Terms & Conditions
**Terms of Service**
*Last Updated: July 7, 2025*

Welcome to the Terms of Service for PeachLust. These Terms apply to the Websites, App, and all related content, tools, and features (“Services”).By using our Services, you agree to these Terms. If you don’t agree, please don’t use the Services. These Terms include important information about your legal rights, so please read them carefully.In these Terms, “you” refers to you as the user, or if you’re using the Services for a company, it includes both you and that company. You confirm you have the authority to agree to these Terms on behalf of that company.

**Note**: Section 8 includes an arbitration clause and class action waiver. By agreeing to these Terms, you agree to resolve disputes through individual arbitration (not in court) and waive your right to join class actions. You can opt out of arbitration as explained in Section 8.

---
## Who Can Use the Services
You must be at least 18 years old to use the Services. Please confirm you meet the age requirement.

---
## Creating and Protecting Your Account
To use certain features, you may need to create an account. You must provide accurate and up-to-date information. You’re responsible for all activity on your Account and keeping your password secure. We’re not responsible for any issues caused by your Account. If you suspect your account has been compromised, email us immediately. Deleted Accounts and their content cannot be recovered.

## Creating and Protecting Your Account
If you purchase or subscribe to paid Services, you agree to pay the fees and taxes in U.S. dollars. If you don’t pay, your access to paid Services will end. We may store your payment method for automatic billing to prevent interruptions. We can change subscription plans or prices at any time, with reasonable notice. Payments must be made in full via credit card, debit card, or other methods we offer. Any payment issues with your bank or processor are your responsibility.

## Subscription Renewals and Cancellations
Subscriptions renew automatically (monthly or as stated on your subscription page) and your payment method will be charged at the start of each period. To stop charges, cancel your subscription through your Account settings. Cancellation takes effect at the end of the current billing period.

---
## Paid Services
If you purchase or subscribe to paid Services, you agree to pay the fees and taxes in U.S. dollars. If you don’t pay, your access to paid Services will end. We may store your payment method for automatic billing to prevent interruptions. We can change subscription plans or prices at any time, with reasonable notice. Payments must be made in full via credit card, debit card, or other methods we offer. Any payment issues with your bank or processor are your responsibility.
Subscription Renewals and Cancellations
Subscriptions renew automatically (monthly or as stated on your subscription page) and your payment method will be charged at the start of each period. To stop charges, cancel your subscription through your Account settings. Cancellation takes effect at the end of the current billing period.

---
## Payment
You can buy physical or digital items (“Offerings”) through the Services. You confirm that your payment information is accurate and you have the right to use it. We may cancel, modify, or limit Offerings at any time. You agree to pay the listed price, and we (or our payment processor, like Stripe) will charge your payment method. All prices are in U.S. dollars, and payment issues with your bank are your responsibility.

## Changes and Pricing
We can change the price, availability, or details of Offerings at any time. While we aim to be accurate, we don’t guarantee that Offering descriptions are error-free. We can correct pricing errors and cancel orders affected by them.

## Order Confirmation
We’ll confirm receipt of your order, but this doesn’t mean we’ve accepted it. We can decline or cancel your order at our discretion. If we cancel after charging you, we’ll refund the amount.

---
## Your Rights to Use the Services
We grant you a personal, worldwide, non-transferable, non-exclusive license to use the Services and download the App (one copy per device). This license lets you use the services as intended, subject to the restrictions below.

**Restrictions**
You may not:  
- Copy, modify, distribute, or sell any part of the Services.  
- Reverse engineer or hack the Services.  
- Use the services for commercial purposes or to send spam.  
- Interfere with or damage the Services.  
- Access others’ Accounts or systems without permission.  
- Bypass security measures or use bots to scrape data.  
- Introduce viruses or harmful materials.  
- Use the Services illegally or unethically.  
- Use the Services in ways not allowed by these Terms.

**Using the App**
You’re responsible for providing the device, internet, and other services needed to use the App. We don’t guarantee the App works on all devices or locations. You may receive push notifications or messages related to the App, and your wireless provider may charge fees for these. You can control notification settings in the App or your device.


---
## Ownership and Content
**Ownership of the Services**
The Services, including their design, text, graphics, and logos, are protected by copyright, trademark, and other laws. We and our licensors own all rights to the Services, except for your content (“Your Content”). You agree not to challenge our ownership.

**Trademarks**
Our name, logo, and related marks are our trademarks or those of our licensors. Other marks on the Services belong to their respective owners.

**Feedback**
If you provide feedback or suggestions, we own them and can use them without paying you or giving you any rights.

**Your Content**
You may post or upload content (“Your Content”). By doing so, you grant us a worldwide, royalty-free, transferable, sublicensable, and irrevocable license to use, store, display, and modify Your Content to operate the Services. This includes sharing your content with others we work with to provide the services or to meet legal requirements. Other users can comment on, tag, or use Your Content as part of their use of the Services, but not for private content. You confirm you have the rights to post Your Content and it doesn’t violate any copyrights or other rights.

**DMCA Policy**
If you believe content on the Services infringes your copyright, notify our copyright agent with:  
- Details of the copyrighted work.  
- Location of the infringing material.  
- Your contact information.  
- A statement that the use isn’t authorized and the information is accurate.  
- Your signature.

---
## Disclaimers, Liability, and Indemnification
**Disclaimers**
You use the Services at your own risk. The Services are provided “as is” and “as available.” We don’t guarantee:  
- The accuracy, security, or reliability of the Services.  
- That the services will be uninterrupted or error-free.  
- Compatibility with your devices.  
- That your content will be stored or transmitted.

No advice from us creates any warranty.

**Liability Limits**
We’re not liable for any damages (e.g., data loss, business interruption) from your use of the Services, even if we knew about the possibility. Our total liability is limited to $100 or what you paid us in the last 6 months, whichever is less. Some jurisdictions may not allow these limits, so they may not apply.

**Indemnification**
You agree to defend and cover any costs or damages we incur due to:  
- Your violation of these Terms or laws.  
- Your violation of others’ rights.  
- Your use of the services.  
- Your Content.  
- Your negligence or misconduct.

---
## Other Terms
**Updating Terms**
We may update these Terms and will update the “Last Revised” date. We’ll try to notify you of major changes (e.g., by email or a Website notice), but it’s your responsibility to check for updates. Using the Services after changes means you accept them.

**Termination**
If you violate these Terms, your license to use the Services ends automatically. We can suspend or delete your Account at any time, with or without reason. If we delete your Account for a violation, you can’t re-register under a new name. We may delete Your Content but aren’t required to. Some Terms survive termination.

**Injunctive Relief**
If you breach these Terms, we can seek equitable relief without proving damages.

---

## Contact Us
If you have any questions, please contact us at
Email: contact@peachlust.com
					`}
						</MarkdownRenderer>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
}
